import React, { useEffect } from 'react';
import { FileText, Scale, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Terms of Service - ContaX';
    
    // Track page view
    if (typeof window !== 'undefined' && (window as any).clarity) {
      (window as any).clarity('set', 'page', 'terms-of-service');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-indigo-100 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to ContaX
          </button>
          
          <div className="flex items-center mb-4">
            <FileText className="w-12 h-12 mr-4" />
            <h1 className="text-4xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-xl text-indigo-100">
            Terms and conditions for using ContaX
          </p>
          <p className="text-indigo-200 mt-2">Last updated: December 2024</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Important Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-12">
          <div className="flex items-start">
            <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-yellow-900 mb-2">Important Notice</h2>
              <p className="text-yellow-800 text-sm">
                ContaX is currently in development. These terms apply to our waitlist and will be updated before product launch. 
                By joining our waitlist, you agree to these terms.
              </p>
            </div>
          </div>
        </div>

        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing our website or joining our waitlist, you agree to be bound by these Terms of Service 
              and our Privacy Policy. If you disagree with any part of these terms, you may not use our services.
            </p>
            <p className="text-gray-700">
              These terms apply to all visitors, users, and others who access or use the service.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              ContaX is a professional networking tool designed to help users remember, organize, and follow up 
              with professional connections. Currently, we are in pre-launch phase offering waitlist signup.
            </p>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Current Services Include:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Waitlist registration for early access</li>
                <li>• Product updates and launch notifications</li>
                <li>• Educational content about professional networking</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts and Registration</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Waitlist Registration</h3>
            <ul className="space-y-3 text-gray-700 mb-6">
              <li><strong>Eligibility:</strong> You must be at least 13 years old to use our services</li>
              <li><strong>Accurate Information:</strong> You must provide accurate and complete information</li>
              <li><strong>Professional Use:</strong> Services are intended for professional networking purposes</li>
              <li><strong>One Account:</strong> One person may maintain only one account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Security</h3>
            <p className="text-gray-700">
              You are responsible for maintaining the confidentiality of your account information and for all 
              activities that occur under your account.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">You May:</h3>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li>• Use the service for legitimate professional networking</li>
              <li>• Share feedback and suggestions for product improvement</li>
              <li>• Unsubscribe from communications at any time</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">You May Not:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Provide false or misleading information</li>
              <li>• Use the service for spam or unsolicited communications</li>
              <li>• Attempt to gain unauthorized access to our systems</li>
              <li>• Use the service for any illegal or unauthorized purpose</li>
              <li>• Interfere with or disrupt the service or servers</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Rights</h3>
            <p className="text-gray-700 mb-4">
              The ContaX service, including its original content, features, and functionality, is owned by ContaX 
              and is protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Rights</h3>
            <p className="text-gray-700 mb-4">
              You retain all rights to any content you provide to us. By using our service, you grant us a 
              license to use this content solely for providing and improving our services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Privacy and Data Protection</h2>
            <p className="text-gray-700 mb-4">
              Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect 
              your information when you use our service.
            </p>
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-blue-900">
                <strong>Key Points:</strong> We encrypt your data, never sell your information, and give you 
                control over your personal data. Read our full 
                <button 
                  onClick={() => navigate('/privacy')}
                  className="text-blue-600 hover:text-blue-800 underline ml-1"
                >
                  Privacy Policy
                </button> for details.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Service Availability</h2>
            <p className="text-gray-700 mb-4">
              We strive to maintain high availability but cannot guarantee uninterrupted service. We may:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Modify or discontinue features with notice</li>
              <li>• Perform scheduled maintenance</li>
              <li>• Experience temporary outages</li>
              <li>• Update our terms with advance notice</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimers and Limitation of Liability</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center">
                <Scale className="w-5 h-5 mr-2" />
                Important Legal Information
              </h3>
              <div className="text-red-800 text-sm space-y-3">
                <p>
                  <strong>Service "As Is":</strong> ContaX is provided "as is" without warranties of any kind, 
                  either express or implied.
                </p>
                <p>
                  <strong>No Guarantee:</strong> We do not guarantee that the service will be error-free, 
                  secure, or available at all times.
                </p>
                <p>
                  <strong>Limitation of Liability:</strong> In no event shall ContaX be liable for any indirect, 
                  incidental, special, or consequential damages.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Rights</h3>
            <p className="text-gray-700 mb-4">
              You may stop using our services at any time and unsubscribe from communications.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Rights</h3>
            <p className="text-gray-700">
              We may terminate or suspend your access immediately if you breach these terms or engage in 
              prohibited activities.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify these terms at any time. We will notify users of material changes via:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Email notification to waitlist members</li>
              <li>• Website announcement</li>
              <li>• Updated "Last Modified" date</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Continued use of our services after changes constitutes acceptance of new terms.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have questions about these Terms of Service, contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <div><strong>Email:</strong> <a href="mailto:legal@contax.com" className="text-blue-600 hover:text-blue-800">legal@contax.com</a></div>
                <div><strong>Subject Line:</strong> Terms of Service Inquiry</div>
                <div><strong>Response Time:</strong> We'll respond within 5 business days</div>
              </div>
            </div>
          </section>
        </div>

        {/* Back to Homepage CTA */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-center text-white mt-12">
          <h3 className="text-2xl font-bold mb-4">Join the ContaX Waitlist</h3>
          <p className="text-indigo-100 mb-6">Be among the first to experience the future of professional networking</p>
          <button
            onClick={() => navigate('/')}
            className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Join Waitlist Now
          </button>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;